import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { ArrowLeft, Loader2, Save, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    useFishingBrandById,
    useUploadBrandLogo,
    useUpsertFishingBrand,
} from './hooks/useFishingBrandsAdmin';
import { FISHING_BRAND_CATEGORIES, FISHING_BRAND_STATUSES } from './types';

const inputClass =
    'w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50';
const labelClass = 'text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 block';

function slugFromName(name: string): string {
    return name
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 120);
}

export function BrandDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === 'new' || !id;
    const { data: brand, isLoading } = useFishingBrandById(isNew ? null : (id ?? null));
    const upsert = useUpsertFishingBrand();
    const upload = useUploadBrandLogo();
    const fileRef = useRef<HTMLInputElement>(null);

    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [slugTouched, setSlugTouched] = useState(false);
    const [countryIso, setCountryIso] = useState('');
    const [countryName, setCountryName] = useState('');
    const [category, setCategory] = useState<string>(FISHING_BRAND_CATEGORIES[0].label);
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [logoBackground, setLogoBackground] = useState<'claro' | 'oscuro'>('claro');
    const [shortDescription, setShortDescription] = useState('');
    const [longDescription, setLongDescription] = useState('');
    const [internalNotes, setInternalNotes] = useState('');
    const [status, setStatus] = useState('borrador');
    const [contentGenerated, setContentGenerated] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (!brand) return;
        setName(brand.name);
        setSlug(brand.slug);
        setSlugTouched(true);
        setCountryIso(brand.country_iso);
        setCountryName(brand.country_name);
        setCategory(brand.category);
        setWebsiteUrl(brand.website_url ?? '');
        setLogoUrl(brand.logo_url);
        setLogoBackground(brand.logo_background);
        setShortDescription(brand.short_description ?? '');
        setLongDescription(brand.long_description ?? '');
        setInternalNotes(brand.internal_notes ?? '');
        setStatus(brand.status);
        setContentGenerated(brand.content_generated);
    }, [brand]);

    const handleUpload = async (files: FileList | null) => {
        if (!files?.[0] || !slug) return;
        try {
            const url = await upload.mutateAsync({ slug, file: files[0] });
            setLogoUrl(url);
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : 'Error al subir el logo');
        }
    };

    const handleSave = async () => {
        setSaveError(null);
        const iso = countryIso.trim().toUpperCase();
        if (!name.trim() || !slug.trim() || iso.length !== 2 || !countryName.trim()) {
            setSaveError('Nombre, slug, país ISO (2 letras) y país son obligatorios.');
            return;
        }
        try {
            const savedId = await upsert.mutateAsync({
                p_id: isNew ? null : id,
                p_slug: slug.trim(),
                p_name: name.trim(),
                p_country_iso: iso,
                p_country_name: countryName.trim(),
                p_category: category,
                p_website_url: websiteUrl,
                p_logo_url: logoUrl,
                p_logo_background: logoBackground,
                p_short_description: shortDescription,
                p_long_description: longDescription,
                p_internal_notes: internalNotes,
                p_status: status,
                p_content_generated: contentGenerated,
            });
            const idMatch = String(savedId).match(
                /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
            );
            if (isNew && idMatch) {
                navigate(`/brands/${idMatch[0]}`);
            } else {
                navigate('/brands');
            }
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : 'Error al guardar');
        }
    };

    if (!isNew && isLoading) {
        return (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Cargando marca…
            </div>
        );
    }

    if (!isNew && !brand) {
        return (
            <div className="text-sm text-muted-foreground">
                Marca no encontrada.{' '}
                <button type="button" className="text-primary" onClick={() => navigate('/brands')}>
                    Volver
                </button>
            </div>
        );
    }

    const storeCount = Number(brand?.store_brand_count ?? 0);
    const clubCount = Number(brand?.club_sponsor_count ?? 0);

    return (
        <div className="space-y-6 max-w-3xl">
            <PageHeader
                title={isNew ? 'Nueva marca' : (brand?.name ?? name)}
                description={isNew ? 'Alta en el catálogo editorial' : `slug · ${brand?.slug ?? slug}`}
            >
                <button
                    type="button"
                    onClick={() => navigate('/brands')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground"
                >
                    <ArrowLeft size={14} />
                    Volver
                </button>
                <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={upsert.isPending}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground"
                >
                    {upsert.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Guardar
                </button>
            </PageHeader>

            {contentGenerated && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                    Descripción generada automáticamente, pendiente de revisión.
                </div>
            )}

            {(brand?.submission_source === 'club_manual' || status === 'revisar') && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100 space-y-1">
                    <p className="font-semibold">
                        {brand?.submission_source === 'club_manual'
                            ? 'Alta manual desde un club — pendiente de revisión'
                            : 'Marca en estado «Revisar»'}
                    </p>
                    <p className="text-amber-900/90 dark:text-amber-200/90">
                        Publica la ficha si es correcta, completa categoría/país, o pásala a borrador /
                        desactívala si hay que retirarla del catálogo.
                    </p>
                </div>
            )}

            {(storeCount > 0 || clubCount > 0) && (
                <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
                    Enlaces automáticos:{' '}
                    {storeCount > 0 && (
                        <span className="text-foreground">{storeCount} marca{storeCount === 1 ? '' : 's'} de tienda (plazos)</span>
                    )}
                    {storeCount > 0 && clubCount > 0 && ' · '}
                    {clubCount > 0 && (
                        <span className="text-foreground">{clubCount} patrocinio{clubCount === 1 ? '' : 's'} de club</span>
                    )}
                    . Los plazos y la escudería de socios no se modifican al editar esta ficha.
                </div>
            )}

            <div className="grid gap-6 md:grid-cols-[180px_1fr]">
                <div className="space-y-2">
                    <span className={labelClass}>Logo</span>
                    <div
                        className={cn(
                            'aspect-square rounded-xl border border-border flex items-center justify-center overflow-hidden',
                            logoBackground === 'oscuro' ? 'bg-[#172A40]' : 'bg-muted/30',
                        )}
                    >
                        {logoUrl ? (
                            <img src={logoUrl} alt={name} className="max-h-[80%] max-w-[80%] object-contain" />
                        ) : (
                            <span className="text-xs text-muted-foreground px-3 text-center">Sin logo · placeholder</span>
                        )}
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={(e) => void handleUpload(e.target.files)}
                    />
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={!slug}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-border text-xs disabled:opacity-40"
                    >
                        <Upload size={12} />
                        {upload.isPending ? 'Subiendo…' : 'Subir / reemplazar'}
                    </button>
                    <select
                        value={logoBackground}
                        onChange={(e) => setLogoBackground(e.target.value as 'claro' | 'oscuro')}
                        className={inputClass}
                    >
                        <option value="claro">Fondo claro</option>
                        <option value="oscuro">Fondo oscuro</option>
                    </select>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className={labelClass}>Nombre</label>
                        <input
                            className={inputClass}
                            value={name}
                            onChange={(e) => {
                                const next = e.target.value;
                                setName(next);
                                if (isNew && !slugTouched) setSlug(slugFromName(next));
                            }}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>Slug</label>
                            <input
                                className={inputClass}
                                value={slug}
                                onChange={(e) => {
                                    setSlugTouched(true);
                                    setSlug(e.target.value);
                                }}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Estado</label>
                            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                                {FISHING_BRAND_STATUSES.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>País ISO</label>
                            <input className={inputClass} value={countryIso} maxLength={2} onChange={(e) => setCountryIso(e.target.value.toUpperCase())} />
                        </div>
                        <div>
                            <label className={labelClass}>País</label>
                            <input className={inputClass} value={countryName} onChange={(e) => setCountryName(e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>Categoría</label>
                        <select className={inputClass} value={category} onChange={(e) => setCategory(e.target.value)}>
                            {FISHING_BRAND_CATEGORIES.map((c) => (
                                <option key={c.slug} value={c.label}>{c.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Web oficial</label>
                        <input className={inputClass} value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
                    </div>
                </div>
            </div>

            <div>
                <label className={labelClass}>Descripción corta ({shortDescription.length}/160)</label>
                <input
                    className={inputClass}
                    value={shortDescription}
                    maxLength={160}
                    onChange={(e) => setShortDescription(e.target.value)}
                />
            </div>
            <div>
                <label className={labelClass}>Descripción larga</label>
                <textarea
                    className={cn(inputClass, 'min-h-[160px]')}
                    value={longDescription}
                    onChange={(e) => setLongDescription(e.target.value)}
                />
            </div>
            <div>
                <label className={labelClass}>Notas internas (no públicas)</label>
                <textarea
                    className={cn(inputClass, 'min-h-[80px]')}
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                    type="checkbox"
                    checked={contentGenerated}
                    onChange={(e) => setContentGenerated(e.target.checked)}
                />
                Marcar descripción como generada (aviso de revisión)
            </label>

            {saveError && <p className="text-sm text-destructive">{saveError}</p>}
        </div>
    );
}
